import React, { useMemo } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import mail from "@/assets/footer/icons/mail.svg";
import office from "@/assets/footer/icons/office.svg";
import description from "@/assets/footer/icons/description.svg";
import { Url } from "@/helpers/url.ts";
import styles from "./FooterElement.module.scss";
import { useLayoutType, LayoutTypes, LayoutStyleTypes } from "@/util-react/layout";
import { Icon } from "@/elements-react/fix-icon";
import { FooterGrid } from "./FooterGrid";
import { ContentGroup, ContentTitle } from "./ContentGroup";
import { FixLink } from "../fix-link";
import { FixBrand } from "../fix-brand";
import { PartnersGroup } from "../partners-group";
import { googleEvent } from "@/helpers/google-events.ts";
import { PageTags } from "../document-elements/page-tags";
import { useServices } from "@/services-context";
import { StaticPagesType } from "@/context/static-pages.ts";
import { B2cFooterContactsType } from "@/services/customer-channel.ts";

export const FooterElement: React.FC = () => {
  const { t } = useTranslation();

  const { layoutType, layoutStyleType } = useLayoutType();
  const { customerChannel, backendTranslations } = useServices();

  const { footerAboutPages, footerContacts, footerContactsAdditional } = customerChannel;

  const variant = useMemo(() => {
    switch (layoutType) {
      case LayoutTypes.TYPE_DESKTOP:
        return "column" as const;
      case LayoutTypes.TYPE_MOBILE:
        return "details" as const;
      default:
        return "column" as const;
    }
  }, [layoutType]);

  const jsonLdDataset = useMemo(
    () => ({
      organization: true,
    }),
    [],
  );

  const linkIgnoreRoute = (text: string, href: string, hrefName: string) => {
    // SSR-safe origin
    const origin = typeof window !== "undefined" ? window.location.origin.replace("www.", "") : "";
    const link = origin + href;
    return (
      <a
        className={classNames(styles.link, styles.linkRouterIgnore)}
        href={link}
        target="_self"
        rel="noopener"
        onClick={(e) => {
          if (window && window.location) {
            googleEvent(`footer_click:${hrefName}`);
            window.location.href = link;
            e.preventDefault();
          }
        }}
      >
        {text}
      </a>
    );
  };

  const link = (text: string, href: string, hrefName: string) => (
    <FixLink className={classNames(styles.link)} type={LayoutStyleTypes.TYPE_MD} href={href} onClick={() => googleEvent(`footer_click:${hrefName}`)}>
      {text}
    </FixLink>
  );

  return (
    <div style={{ display: "contents" }}>
      <FooterGrid layout={layoutType}>
        <PartnersGroup slot="top" />

        <div slot="main" className={classNames(styles.group, styles.main)}>
          <div className={classNames(styles.fixBrandWrapper)}>
            <FixLink className="item" href={Url.to("home-page-with-lang")} ariaLabel="logo" onClick={() => googleEvent("footer_click:home-page")}>
              <FixBrand type={layoutStyleType} iconWidth={100} />
            </FixLink>
          </div>

          <div className={styles.text}>{backendTranslations.footerText}</div>
        </div>

        <ContentGroup
          slot="service"
          title={t("g78tgk7885234se9.footer-customer-service", "Customer service")}
          variant={variant}
          className={classNames(styles.group, styles.menu)}
        >
          {linkIgnoreRoute(t("o9n2uwt3nza3u3ri.my-account", "My account"), "/business-v2", "my-account")}

          {footerAboutPages.includes(StaticPagesType.help) &&
            link(t("s6e6f5foojcu719q.warranty", "Warranty"), Url.to("help-page", { section: "warranty-and-returns" }), "warranty")}
        </ContentGroup>

        <ContentGroup
          slot="information"
          title={t("gj48392kk3058vn2.footer-about", "About us")}
          variant={variant}
          className={classNames(styles.group, styles.menu)}
        >
          {footerAboutPages.includes(StaticPagesType.aboutUs) && link(t("kh08f0scyoc3nfp0.company", "Company"), Url.to("about-us-page"), "company")}

          {footerAboutPages.includes(StaticPagesType.whoWeAre) && link(t("j70139vjmms2490f.whoWeAre", "Who we are"), Url.to("who-we-are-page"), "who-we-are")}
          {footerAboutPages.includes(StaticPagesType.whatWeDo) && link(t("4kw4rc01m4c94cns.what-we-do", "What we do"), Url.to("what-we-do-page"), "what-we-do")}
          {footerAboutPages.includes(StaticPagesType.whoWeServe) &&
            link(t("zv7t2q5m8n1c4r6k.who-we-serve", "Who we serve"), Url.to("who-we-serve-page"), "who-we-serve")}

          {footerAboutPages.includes(StaticPagesType.productRange) &&
            link(t("snv9qp34igffq94qh.product-range", "Product range"), Url.to("product-range-page"), "product-range")}

          {footerAboutPages.includes(StaticPagesType.whyCustomersTrustUs) &&
            link(t("94kf48j3f729584n.whyCustomersTrust", "Why customers trust us"), Url.to("why-customers-trust-us-page"), "why-customers-trust-us")}

          {footerAboutPages.includes(StaticPagesType.help) && link(t("1h06pmbvy0noasg3.help", "Help"), Url.to("help-page"), "help")}
          {footerAboutPages.includes(StaticPagesType.privacyPolicy) &&
            link(t("83p9sscfj2b6t121.privacy-policy", "Privacy policy"), Url.to("privacy-policy-page"), "privacy-policy")}
          {footerAboutPages.includes(StaticPagesType.paymentTerms) && link(t("otstyqf59gqt6d8z.payment", "Payment"), Url.to("payment-terms-page"), "payment")}
        </ContentGroup>

        {layoutType === LayoutTypes.TYPE_DESKTOP && footerContacts?.hasAnyContactFieldFilled && (
          <ContentGroup
            slot="contacts"
            title={footerContacts?.businessName ?? t("9d7866wn3k1o4w68.footer-contacts", "Contacts")}
            variant={variant}
            className={classNames(styles.group, styles.contacts, styles[layoutType])}
          >
            <div className={classNames(styles.contactRows)}>
              {<FooterContactsAddress data={footerContacts} />}

              {<FooterContactsRegistration data={footerContacts} />}

              {<FooterContactsEmail data={footerContacts} />}
            </div>
          </ContentGroup>
        )}

        {layoutType === LayoutTypes.TYPE_DESKTOP && footerContactsAdditional?.hasAnyContactFieldFilled && (
          <ContentGroup
            slot="contactsAdditional"
            title={footerContactsAdditional?.businessName ?? ""}
            variant={variant}
            className={classNames(styles.group, styles.contacts, styles[layoutType])}
          >
            <div className={classNames(styles.contactRows)}>
              {<FooterContactsAddress data={footerContactsAdditional} />}

              {<FooterContactsRegistration data={footerContactsAdditional} />}

              {<FooterContactsEmail data={footerContactsAdditional} />}
            </div>
          </ContentGroup>
        )}

        {layoutType === LayoutTypes.TYPE_MOBILE && (footerContacts?.hasAnyContactFieldFilled || footerContactsAdditional?.hasAnyContactFieldFilled) && (
          <ContentGroup
            slot="contacts"
            title={t("9d7866wn3k1o4w68.footer-contacts", "Contacts")}
            variant={variant}
            className={classNames(styles.group, styles.contacts)}
          >
            {footerContacts?.hasAnyContactFieldFilled && (
              <div>
                {footerContacts?.businessName && <ContentTitle title={footerContacts.businessName} />}

                <div className={classNames(styles.contactRows)}>
                  {<FooterContactsAddress data={footerContacts} />}

                  {<FooterContactsRegistration data={footerContacts} />}

                  {<FooterContactsEmail data={footerContacts} />}
                </div>
              </div>
            )}

            {footerContactsAdditional?.hasAnyContactFieldFilled && (
              <div>
                {footerContactsAdditional?.businessName && <ContentTitle title={footerContactsAdditional.businessName} />}

                <div className={classNames(styles.contactRows)}>
                  {<FooterContactsAddress data={footerContactsAdditional} />}

                  {<FooterContactsRegistration data={footerContactsAdditional} />}

                  {<FooterContactsEmail data={footerContactsAdditional} />}
                </div>
              </div>
            )}
          </ContentGroup>
        )}
      </FooterGrid>
      <PageTags jsonld={jsonLdDataset} />
    </div>
  );
};

interface FooterContactsProps {
  data: B2cFooterContactsType;
}

const FooterContactsEmail: React.FC<FooterContactsProps> = ({ data }) => {
  if (!data?.email) {
    return null;
  }

  return (
    <div className={classNames(styles.row, styles.text)}>
      <Icon src={mail} className={styles.icon} width={19} />
      <div>{data.email}</div>
    </div>
  );
};

const FooterContactsAddress: React.FC<FooterContactsProps> = ({ data }) => {
  if (!data?.address) {
    return null;
  }

  return (
    <div className={classNames(styles.row, styles.text)}>
      <Icon src={office} className={styles.icon} width={19} />
      <div>{data.address}</div>
    </div>
  );
};

const FooterContactsRegistration: React.FC<FooterContactsProps> = ({ data }) => {
  if (!data?.regNr && !data?.vatNr) {
    return null;
  }

  return (
    <div className={classNames(styles.row, styles.text, styles.description)}>
      <Icon src={description} className={styles.icon} width={19} />
      <div>
        {data?.regNr && <div className={styles.reg}>Reg.nr. {data.regNr}</div>}
        {data?.vatNr && <div className={styles.vat}>VAT.Nr. {data.vatNr}</div>}
      </div>
    </div>
  );
};
